// DESCRIPTION: Step 5 — payment method. Choose Stripe or offline payments.
// Stripe shows key inputs; offline is one click.

import { __ } from '@wordpress/i18n';
import WizardShell from '../components/WizardShell';
import ChoiceCard from '../components/ChoiceCard';

/**
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {Object}   props.value       { method, stripe_publishable_key, stripe_secret_key }
 * @param {Function} props.onChange    (next) => void
 * @param {Function} props.onBack
 * @param {Function} props.onSkip      Skip this step.
 * @param {Function} props.onSkipSetup Skip the entire setup.
 * @param {Function} props.onContinue
 * @param {number}   props.progress
 * @param {boolean}  props.isLoading
 *
 * @return {JSX.Element}
 */
export default function PaymentsStep( {
	value,
	onChange,
	onBack,
	onSkip,
	onSkipSetup,
	onContinue,
	progress,
	isLoading,
} ) {
	const update = ( key, next ) => onChange( { ...value, [ key ]: next } );

	return (
		<WizardShell
			stepKey="payments"
			progress={ progress }
			onBack={ onBack }
			onSkip={ onSkip }
			onSkipSetup={ onSkipSetup }
			onContinue={ onContinue }
			continueLabel={ __( 'Finish setup', 'nemtly-booking' ) }
			isLoading={ isLoading }
		>
			<div className="nemtly-wizard-step">
				<h2 className="nemtly-wizard-headline">
					{ __( 'How will customers pay?', 'nemtly-booking' ) }
				</h2>
				<p className="nemtly-wizard-lede">
					{ __(
						'Pick one to get started. You can add more methods later.',
						'nemtly-booking'
					) }
				</p>

				<div className="nemtly-wizard-grid nemtly-wizard-grid--2">
					<ChoiceCard
						title={ __( 'Connect Stripe', 'nemtly-booking' ) }
						description={ __(
							'Accept cards online via Stripe.',
							'nemtly-booking'
						) }
						isActive={ value.method === 'stripe' }
						onClick={ () => update( 'method', 'stripe' ) }
					/>
					<ChoiceCard
						title={ __(
							'Offline payments only',
							'nemtly-booking'
						) }
						description={ __(
							'Customers pay you directly — cash, bank transfer, etc.',
							'nemtly-booking'
						) }
						isActive={ value.method === 'offline' }
						onClick={ () => update( 'method', 'offline' ) }
					/>
				</div>

				{ value.method === 'stripe' && (
					<div className="nemtly-wizard-substep">
						<div className="nemtly-wizard-field">
							<label htmlFor="nemtly-wizard-stripe-pk">
								{ __(
									'Stripe Publishable Key',
									'nemtly-booking'
								) }
							</label>
							<input
								id="nemtly-wizard-stripe-pk"
								type="text"
								value={ value.stripe_publishable_key }
								onChange={ ( e ) =>
									update(
										'stripe_publishable_key',
										e.target.value
									)
								}
								placeholder="pk_live_…"
							/>
						</div>
						<div className="nemtly-wizard-field">
							<label htmlFor="nemtly-wizard-stripe-sk">
								{ __( 'Stripe Secret Key', 'nemtly-booking' ) }
							</label>
							<input
								id="nemtly-wizard-stripe-sk"
								type="password"
								value={ value.stripe_secret_key }
								onChange={ ( e ) =>
									update(
										'stripe_secret_key',
										e.target.value
									)
								}
								placeholder="sk_live_…"
							/>
						</div>
						<p className="nemtly-wizard-hint">
							<a
								href="https://dashboard.stripe.com/apikeys"
								target="_blank"
								rel="noopener noreferrer"
							>
								{ __(
									'Where do I find these?',
									'nemtly-booking'
								) }
							</a>
						</p>
					</div>
				) }

				<p className="nemtly-wizard-fineprint">
					{ __(
						'You can add or change payment methods later in Settings → Payments.',
						'nemtly-booking'
					) }
				</p>
			</div>
		</WizardShell>
	);
}
